import {announce} from '@github-ui/aria-live'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {use, useCallback, useEffect} from 'react'

import {
  githubReference,
  githubReferenceDetails,
  implicitAttachmentIcon,
  implicitAttachmentKey,
  implicitAttachmentUrl,
  REFERENCE_TYPE_ANNOUNCE_LABEL,
} from '../attachments/attachment-refs'
import {parsePastedGitHubReference} from '../attachments/parse-github-reference-url'
import {createRepositoryFileAttachment} from '../attachments/repository-file-attachment'
import {FileAttachContext, type FileAttachParams} from '../contexts/FileAttachContext'
import {ReferenceAttachContext, type ReferenceAttachParams} from '../contexts/ReferenceAttachContext'
import type {ComposerAttachmentsController} from './use-composer-attachments'

const RESOURCE_PANEL_ATTACHMENT_PREFIX = 'resource-panel-file:'
const RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX = 'resource-panel-reference:'

export function ComposerFileAttachBridge({
  attachments,
  writable,
  pastedReferences,
}: {
  attachments: ComposerAttachmentsController
  writable: boolean
  pastedReferences?: ComposerAttachmentsController['pastedReferences']
}) {
  useComposerFileAttach(attachments, writable, pastedReferences)
  return null
}

export function useComposerFileAttach(
  attachments: ComposerAttachmentsController,
  writable: boolean,
  explicitPastedReferences?: ComposerAttachmentsController['pastedReferences'],
) {
  const fileAttachRef = use(FileAttachContext)
  const referenceAttachRef = use(ReferenceAttachContext)
  const addTrackedAttachments = attachments.addTrackedAttachments
  const removeTrackedAttachmentsByPrefix = attachments.removeTrackedAttachmentsByPrefix
  const pastedReferences = explicitPastedReferences ?? attachments.pastedReferences
  const tracked = attachments.tracked
  const attach = useCallback(
    (params: FileAttachParams) => {
      if (!writable) return
      addTrackedAttachments(
        [
          createRepositoryFileAttachment(
            {
              repo: {
                id: params.repoId,
                name: `${params.repoOwner}/${params.repoName}`,
                private: params.repoPrivate ?? true,
              },
              path: params.path,
              type: 'file',
              ref: params.ref,
              commitOID: params.commitOID,
            },
            RESOURCE_PANEL_ATTACHMENT_PREFIX,
          ),
        ],
        {replacePrefix: RESOURCE_PANEL_ATTACHMENT_PREFIX},
      )
    },
    [addTrackedAttachments, writable],
  )
  useEffect(() => {
    if (!fileAttachRef || !writable) return
    let active = true
    const handler = (params: FileAttachParams) => {
      if (active) attach(params)
    }
    fileAttachRef.current = handler
    return () => {
      active = false
      if (fileAttachRef.current === handler) fileAttachRef.current = null
    }
  }, [attach, fileAttachRef, writable])

  const attachReference = useCallback(
    (params: ReferenceAttachParams | null) => {
      if (!writable) return
      if (!params) {
        const existing = tracked.find(item => item.token.id.startsWith(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX))
        if (!existing) return
        removeTrackedAttachmentsByPrefix(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX)
        const details = githubReferenceDetails(existing.ahp)
        if (details) announce(`Removed ${REFERENCE_TYPE_ANNOUNCE_LABEL[details.referenceType]} #${details.number}`)
        return
      }
      let parsed = parsePastedGitHubReference(params.url, ssrSafeLocation.origin)
      if (!parsed && URL.canParse(params.url, ssrSafeLocation.origin)) {
        const canonicalUrl = new URL(params.url, ssrSafeLocation.origin)
        canonicalUrl.search = ''
        canonicalUrl.hash = ''
        parsed = parsePastedGitHubReference(canonicalUrl.toString(), ssrSafeLocation.origin)
      }
      const referenceType = parsed?.type ?? params.referenceType
      const number = parsed?.number ?? params.number
      const attachment = githubReference(referenceType, number, parsed?.url ?? params.url, params.title, params.state)
      if (!attachment) return
      const url = implicitAttachmentUrl(attachment)
      if (!url) return
      if (
        pastedReferences.some(
          reference => implicitAttachmentKey(reference.ahp) === implicitAttachmentKey(attachment),
        ) ||
        tracked.some(
          item =>
            !item.token.id.startsWith(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX) &&
            implicitAttachmentUrl(item.ahp) === url,
        )
      ) {
        const existing = tracked.find(item => item.token.id.startsWith(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX))
        if (!existing) return
        removeTrackedAttachmentsByPrefix(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX)
        if (implicitAttachmentUrl(existing.ahp) !== url) {
          const details = githubReferenceDetails(existing.ahp)
          if (details) announce(`Removed ${REFERENCE_TYPE_ANNOUNCE_LABEL[details.referenceType]} #${details.number}`)
        }
        return
      }
      const alreadyAttached = tracked.some(item => implicitAttachmentUrl(item.ahp) === url)
      const replaced = tracked.find(
        item =>
          item.token.id.startsWith(RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX) &&
          implicitAttachmentUrl(item.ahp) !== url,
      )
      const replacedDetails = replaced ? githubReferenceDetails(replaced.ahp) : undefined
      addTrackedAttachments(
        [
          {
            token: {
              id: `${RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX}${url}`,
              label: attachment.label,
              icon: implicitAttachmentIcon(attachment),
              url,
            },
            ahp: attachment,
          },
        ],
        {replacePrefix: RESOURCE_PANEL_REFERENCE_ATTACHMENT_PREFIX},
      )
      if (!alreadyAttached) {
        const attachedLabel = `${REFERENCE_TYPE_ANNOUNCE_LABEL[referenceType]} #${number}`
        if (replacedDetails) {
          const removedLabel = `${REFERENCE_TYPE_ANNOUNCE_LABEL[replacedDetails.referenceType]} #${replacedDetails.number}`
          announce(`Attached ${attachedLabel}, removed ${removedLabel}`)
        } else {
          announce(`Attached ${attachedLabel}`)
        }
      }
    },
    [addTrackedAttachments, pastedReferences, removeTrackedAttachmentsByPrefix, tracked, writable],
  )
  useEffect(() => {
    if (!referenceAttachRef || !writable) return
    let active = true
    const handler = (params: ReferenceAttachParams | null) => {
      if (active) attachReference(params)
    }
    referenceAttachRef.current = handler
    return () => {
      active = false
      if (referenceAttachRef.current === handler) referenceAttachRef.current = null
    }
  }, [attachReference, referenceAttachRef, writable])
}
